// Slide mechanics: preview + apply. Spec: docs/mechanics.md §3, §6 W3 (gravity).
import { coordKey, step } from './hex.js';
import { cellStatus, getTile, isBlocker, moveTile, replaceTile } from './board.js';
import { resolveMerges } from './merge.js';
// Maximum chained post-slide effects (gravity OR wind re-slides) per
// originating player slide. Defends against author-error cycles the
// generator missed. Production content is generator-screened and never
// approaches this limit; if this guard ever fires in user-facing play,
// treat as a P1 content bug.
const GRAVITY_GUARD_STEPS = 6;
// W17 Gale: given a tile's current coord, find a wind cell adjacent to
// the tile that's pushing toward the tile's position. Returns the wind
// direction (which is the direction the tile should be pushed further),
// or null if no applicable wind exists.
//
// Geometry: a wind cell at A with direction D pushes tiles that land at
// step(A, D). So if we're checking tile-at-T, we want wind cells W where
// step(W, dir(W)) === T. Iterating board.wind (typically 1-3 entries) is
// cheaper than iterating the 6 directions.
//
// If multiple wind cells happen to push toward the same tile (rare but
// possible in dense Gale layouts), the FIRST match wins. Iteration order
// of board.wind is insertion order (Map semantics) which mirrors the
// LevelDef.wind authoring order — deterministic per level.
function findWindPushDir(board, tileCoord) {
    if (board.wind.size === 0)
        return null;
    for (const [windCoordKeyStr, windDir] of board.wind) {
        const [qStr, rStr] = windCoordKeyStr.split(',');
        const windCell = { q: Number(qStr), r: Number(rStr) };
        const target = step(windCell, windDir);
        if (target.q === tileCoord.q && target.r === tileCoord.r) {
            return windDir;
        }
    }
    return null;
}
// Compute where a tile lands given board + direction, without mutating.
// Returns null if the slide is illegal (no movement possible or tile unmovable).
export function computeSlideTarget(board, tileId, direction) {
    const tile = board.tiles.get(tileId);
    if (!tile)
        return null;
    // Locked and mixer tiles cannot be slid (mechanics §3 + world rules).
    if (tile.state.kind === 'locked' || tile.state.kind === 'mixer')
        return null;
    let cur = tile.coord;
    let last = cur;
    while (true) {
        const next = step(cur, direction);
        const status = cellStatus(board, next);
        if (status !== 'in')
            break; // hit edge or hole
        if (board.tileAt.has(coordKey(next)) && coordKey(next) !== coordKey(tile.coord))
            break; // hit tile
        last = next;
        cur = next;
    }
    if (coordKey(last) === coordKey(tile.coord))
        return null; // no movement
    return { from: tile.coord, to: last };
}
// W13 "Crucible" mechanic: if a sliding tile stopped because a MIXER tile was
// in the cell beyond the destination, the slid tile just bumped it. Transform
// the mixer into a normal tile of the slid tile's color — the mixer is now
// "claimed." Idempotent on non-mixer cells (no-op if no blocker, or if the
// blocker is not a mixer).
//
// Reused by both the player-driven slide and W14 gravity re-slides — a
// re-slide that bumps a mixer claims it identically.
function claimMixerIfBumped(board, slidTileId, direction) {
    const slidTile = board.tiles.get(slidTileId);
    if (!slidTile)
        return board;
    const beyond = step(slidTile.coord, direction);
    const blocker = getTile(board, beyond);
    if (blocker && blocker.state.kind === 'mixer') {
        return replaceTile(board, blocker.id, {
            color: slidTile.color,
            state: { kind: 'normal' },
        });
    }
    return board;
}
// W14 "Ballast" mechanic: after a tile lands (and post-merge), if it's still
// on the board AND on a gravity cell, re-slide it in the arrow's direction.
// Each re-slide runs its own merge resolution, so chains can fire from gravity
// landings. Counts as part of the originating move (the move counter is
// incremented once by applySlide, regardless of how many gravity steps fire).
//
// Tiles that don't trigger gravity:
// - Tiles consumed by merges (the originating tileId is gone).
// - Locked / mixer / fragile-states (cannot slide; computeSlideTarget rejects).
// - Tiles produced by merges that happen to spawn on a gravity cell — they
//   weren't slid into that cell, so spec §6 W3's "ends a slide" trigger does
//   NOT apply. (We track gravity for the originating tileId only.)
//
// Termination: every step either moves the tile to a new cell or exits. With
// the generator's static acyclic check on gravity placements + the in-engine
// hard cap, chained gravity cannot loop indefinitely.
//
// Returns { board, cycled }. cycled=true means the loop hit the guard cap —
// a transient cycle that the generator's static (empty-board) check missed
// because actual tile placement enabled it. We do NOT throw: production play
// gets a deterministic-if-weird outcome rather than a crash, and the solver
// can mark cyclic moves as invalid via the cycled flag during search.
// Generator screening (see propose.ts and rate.ts) rejects any level whose
// solver pass encounters cycled=true on any reachable move.
// Post-slide loop. Handles both W14 gravity (tile-on-cell trigger) AND W17
// wind (tile-adjacent-to-cell trigger). Each iteration prefers gravity over
// wind when both could fire — gravity is the existing well-tested mechanic
// so we don't change its semantics. The 6-step guard covers cycles in
// either OR combinations of both.
//
// Why prefer gravity: a tile sitting on a gravity cell will always re-slide
// per the cell's direction. The wind nearby might also have wanted to push
// it, but gravity wins. Authors who set up overlapping gravity+wind get
// gravity behavior. That's a design constraint, not a bug.
function gravityLoop(initial, slidTileId) {
    let cur = initial;
    for (let i = 0; i < GRAVITY_GUARD_STEPS; i++) {
        const tile = cur.tiles.get(slidTileId);
        if (!tile)
            return { board: cur, cycled: false }; // tile consumed by a merge
        if (tile.state.kind !== 'normal' && tile.state.kind !== 'charged')
            return { board: cur, cycled: false };
        // Gravity has priority; check it first.
        let pushDir = cur.gravity.get(coordKey(tile.coord));
        // No gravity at this cell? Fall through to wind check.
        if (!pushDir) {
            pushDir = findWindPushDir(cur, tile.coord) ?? undefined;
        }
        if (!pushDir)
            return { board: cur, cycled: false }; // no post-slide effect
        const tgt = computeSlideTarget(cur, slidTileId, pushDir);
        if (!tgt)
            return { board: cur, cycled: false }; // tile is wedged
        cur = moveTile(cur, slidTileId, tgt.to);
        cur = claimMixerIfBumped(cur, slidTileId, pushDir);
        cur = resolveMerges(cur, slidTileId).board;
    }
    // Reached the guard cap. Generator should have prevented this; if it didn't,
    // production play still gets a deterministic outcome (the board after exactly
    // 6 post-slide steps), and the solver/generator can use cycled=true to filter.
    return { board: cur, cycled: true };
}
export function applySlide(board, tileId, direction) {
    const tgt = computeSlideTarget(board, tileId, direction);
    if (!tgt) {
        return { board, movesUsed: 0, merges: [] };
    }
    const movingTile = board.tiles.get(tileId);
    if (!movingTile)
        return { board, movesUsed: 0, merges: [] };
    let next = moveTile(board, tileId, tgt.to);
    next = claimMixerIfBumped(next, tileId, direction);
    const resolved = resolveMerges(next, tileId);
    next = resolved.board;
    // W14: if the slid tile (still on the board) landed on a gravity cell,
    // chain gravity re-slides until it stops. Merges are collected only from
    // the player slide; gravity-step merges resolve onto the board but are
    // not surfaced via ApplyResult.merges (the UI animates from board diffs,
    // not from the merges list). If we later need per-step merge events for
    // analytics or replay, plumb a merges accumulator through gravityLoop.
    const gravResult = gravityLoop(next, tileId);
    return {
        board: gravResult.board,
        movesUsed: 1,
        merges: resolved.events,
        ...(gravResult.cycled ? { cycled: true } : {}),
    };
}
// Preview: pure, returns the same post-resolution board that applySlide would produce.
// Invariant (mechanics §3.1): previewSlide and applySlide must agree byte-for-byte.
export function previewSlide(board, tileId, direction) {
    const tile = board.tiles.get(tileId);
    const tgt = computeSlideTarget(board, tileId, direction);
    if (!tgt || !tile) {
        const from = tile?.coord ?? { q: 0, r: 0 };
        return {
            legal: false,
            reason: tile ? 'blocked' : 'no-tile',
            from,
            to: from,
            direction,
            mergesPreview: [],
            resultBoard: board,
        };
    }
    const applied = applySlide(board, tileId, direction);
    return {
        legal: true,
        from: tgt.from,
        to: tgt.to,
        direction,
        mergesPreview: applied.merges,
        resultBoard: applied.board,
    };
}
// Convenience: all legal directions for a tile.
export function legalDirections(board, tileId) {
    const dirs = ['E', 'NE', 'NW', 'W', 'SW', 'SE'];
    return dirs.filter((d) => computeSlideTarget(board, tileId, d) !== null);
}
// Suppress unused import (isBlocker) — keep for later objective work.
void isBlocker;
